import { PocButton, PocIcon, PocProgressSpinner } from 'commons/components/components-wrapper';
import { useTranslation } from 'core/i18n/i18n';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useOverflowFilters } from './use-overflow-filters.hook';
import { TableFiltersActionsLayout, TableFiltersLayout, TableFiltersOptionsLayout } from './table-filters.style';

export interface TableFiltersRef {
    element: HTMLDivElement | null;
    getHeight: () => number;
}

export interface TableFiltersComponentProps extends React.PropsWithChildren {
    showResetAction?: boolean;
    showSaveAction?: boolean;
    disabledSaveAction?: boolean;
    showUpdateAction?: boolean;
    disableUpdateAction?: boolean;
    updateActionLoading?: boolean;
    isVisible?: boolean;
    onResetAction?: VoidFunction;
    onSaveAction?: VoidFunction;
    onUpdateAction?: VoidFunction;
    onShowAllChange?: (showAll: boolean) => void;
}

export const TableFiltersComponent = forwardRef<TableFiltersRef, TableFiltersComponentProps>((props, ref) => {
    const {
        children,
        showResetAction,
        showSaveAction,
        showUpdateAction,
        disabledSaveAction,
        disableUpdateAction,
        updateActionLoading = false,
        isVisible = true,
        onResetAction,
        onSaveAction,
        onUpdateAction,
        onShowAllChange,
    } = props;

    const { t } = useTranslation();
    const internalRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(
        ref,
        () => ({
            element: internalRef.current,
            getHeight: () => internalRef.current?.offsetHeight ?? 0,
        }),
        []
    );

    useEffect(() => {
        if (!isVisible) {
            internalRef.current?.querySelectorAll('owc-menu').forEach((filterMenu: Element) => {
                filterMenu.setAttribute('visible', 'false');
            });
            internalRef.current?.querySelectorAll('input').forEach((filter: HTMLInputElement) => {
                filter.blur();
            });
        }
    }, [isVisible]);

    const { containerRef, hasOverflow, showAllFilters, hiddenCount, toggleShowMore, isInitialized } =
        useOverflowFilters({
            threshold: 22.5,
            debounceTime: 150,
        });

    useEffect(() => {
        onShowAllChange?.(showAllFilters);
    }, [onShowAllChange, showAllFilters]);

    return (
        <TableFiltersLayout ref={internalRef} visible={isVisible}>
            <TableFiltersOptionsLayout ref={containerRef} className={showAllFilters ? 'show-all' : ''}>
                {children}
            </TableFiltersOptionsLayout>
            <TableFiltersActionsLayout>
                {hasOverflow && isInitialized && (
                    <PocButton variant="tertiary" onClick={toggleShowMore}>
                        {showAllFilters ? (
                            <PocIcon slot="prefix" name="chevron_up" />
                        ) : (
                            <PocIcon slot="prefix" name="chevron_down" />
                        )}
                        {showAllFilters
                            ? t('commons.components.table.table-filters.actions.button.showLess')
                            : t('commons.components.table.table-filters.actions.button.showMore', {
                                  number: hiddenCount,
                              })}
                    </PocButton>
                )}
                {showResetAction ? (
                    <PocButton data-testid="table-filters-reset" variant="tertiary" onClick={onResetAction}>
                        {showUpdateAction
                            ? t('commons.components.table.table-filters.actions.button.restore')
                            : t('commons.components.table.table-filters.actions.button.reset')}
                    </PocButton>
                ) : undefined}
                {showSaveAction ? (
                    <PocButton
                        data-testid="table-filters-save"
                        variant="tertiary"
                        disabled={disabledSaveAction}
                        onClick={onSaveAction}
                    >
                        {t('commons.components.table.table-filters.actions.button.save')}
                    </PocButton>
                ) : undefined}
                {showUpdateAction ? (
                    <PocButton
                        data-testid="table-filters-update"
                        variant="tertiary"
                        disabled={disableUpdateAction || updateActionLoading}
                        onClick={onUpdateAction}
                    >
                        {updateActionLoading ? (
                            <PocProgressSpinner size="xSmall" style={{ display: 'block' }} />
                        ) : (
                            t('commons.components.table.table-filters.actions.button.update')
                        )}
                    </PocButton>
                ) : undefined}
            </TableFiltersActionsLayout>
        </TableFiltersLayout>
    );
});

TableFiltersComponent.displayName = 'TableFiltersComponent';
